/* eslint-disable @tanstack/query/prefer-query-options */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  CreditCard,
  FileText,
  LockKeyhole,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react'
import { type FocusEvent, useState } from 'react'
import { toast } from 'sonner'

import { PageHeader } from '#components/layout/page-header'
import { Badge } from '#components/ui/badge'
import { Button } from '#components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '#components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '#components/ui/dialog'
import { Input } from '#components/ui/input'
import { Label } from '#components/ui/label'
import { Skeleton } from '#components/ui/skeleton'
import { Spinner } from '#components/ui/spinner'
import { useAuth } from '#hooks/use-auth'
import { type BillingProfile, billingService } from '#services/billing'
import { hasBlockingQueryError } from '#utils/query-state'

export const Route = createFileRoute('/_auth/_admin/planos')({
  head: () => ({
    meta: [
      { title: 'Plano e assinatura | UseComanda' },
      {
        name: 'description',
        content: 'Gerencie dados fiscais, plano e assinatura.',
      },
    ],
  }),
  component: PlansPage,
})

const fields: Array<{
  name: keyof BillingProfile
  label: string
  placeholder: string
  span?: boolean
}> = [
  { name: 'cpf_cnpj', label: 'CPF ou CNPJ', placeholder: '00.000.000/0000-00' },
  {
    name: 'billing_email',
    label: 'E-mail financeiro',
    placeholder: 'financeiro@empresa.com.br',
  },
  { name: 'phone', label: 'Telefone', placeholder: '(00) 00000-0000' },
  { name: 'cep', label: 'CEP', placeholder: '00000-000' },
  {
    name: 'logradouro',
    label: 'Endereço',
    placeholder: 'Rua, avenida…',
    span: true,
  },
  { name: 'numero', label: 'Número', placeholder: '123' },
  { name: 'bairro', label: 'Bairro', placeholder: 'Centro' },
  { name: 'cidade', label: 'Cidade', placeholder: 'Sua cidade' },
  { name: 'uf', label: 'UF', placeholder: 'SP' },
]

function getStatus(status?: string) {
  if (status === 'active')
    return {
      label: 'Plano ativo',
      description: 'Sua operação está protegida e funcionando normalmente.',
      tone: 'success' as const,
    }
  if (status === 'pending')
    return {
      label: 'Pagamento pendente',
      description: 'Conclua o pagamento para ativar todos os recursos.',
      tone: 'warning' as const,
    }
  if (status === 'past_due')
    return {
      label: 'Pagamento atrasado',
      description: 'Regularize a assinatura para evitar interrupções.',
      tone: 'danger' as const,
    }
  if (status === 'canceled')
    return {
      label: 'Plano cancelado',
      description: 'Reative para continuar usando a operação completa.',
      tone: 'danger' as const,
    }
  return {
    label: 'Plano não ativado',
    description: 'Ative o UseComanda para colocar sua operação em movimento.',
    tone: 'neutral' as const,
  }
}

async function fillAddressFromCep(event: FocusEvent<HTMLInputElement>) {
  const cep = event.currentTarget.value.replace(/\D/g, '')
  const form = event.currentTarget.form
  if (cep.length !== 8 || !form) return
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
    const data = (await response.json()) as {
      erro?: boolean
      logradouro?: string
      bairro?: string
      localidade?: string
      uf?: string
    }
    if (data.erro) return
    const values = {
      logradouro: data.logradouro,
      bairro: data.bairro,
      cidade: data.localidade,
      uf: data.uf,
    }
    Object.entries(values).forEach(([name, value]) => {
      const input = form.elements.namedItem(name)
      if (input instanceof HTMLInputElement && value) input.value = value
    })
  } catch {
    // Manual address entry remains available when the lookup service is offline.
  }
}

function BillingForm({
  profile,
  tenantId,
  onClose,
  canCancel,
}: {
  profile: BillingProfile
  tenantId: string
  onClose: () => void
  canCancel: boolean
}) {
  const client = useQueryClient()
  const update = useMutation({
    mutationFn: (form: HTMLFormElement) =>
      billingService.update(
        tenantId,
        Object.fromEntries(new FormData(form)) as Record<string, string>,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['billing'] })
      onClose()
      toast.success('Dados de faturamento atualizados.')
    },
    onError: (error: Error) =>
      toast.error('Não foi possível salvar.', { description: error.message }),
  })
  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate(event.currentTarget)
      }}
    >
      {fields.map((field) => (
        <div
          key={field.name}
          className={`space-y-2 ${field.span ? 'sm:col-span-2' : ''}`}
        >
          <Label htmlFor={field.name}>{field.label}</Label>
          <Input
            id={field.name}
            name={field.name}
            required
            placeholder={field.placeholder}
            defaultValue={profile[field.name] ?? ''}
            onBlur={field.name === 'cep' ? fillAddressFromCep : undefined}
          />
        </div>
      ))}
      <div className="flex justify-end gap-2 border-t pt-4 sm:col-span-2">
        {canCancel && (
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button disabled={update.isPending}>
          {update.isPending && <Spinner data-icon="inline-start" />}
          {update.isPending ? 'Salvando…' : 'Salvar dados'}
        </Button>
      </div>
    </form>
  )
}

function PlansPage() {
  const { tenantId } = useAuth()
  const [billingOpen, setBillingOpen] = useState(false)
  const profile = useQuery({
    queryKey: ['billing', 'profile', tenantId],
    queryFn: () => billingService.profile(tenantId!),
    enabled: !!tenantId,
  })
  const subscription = useQuery({
    queryKey: ['billing', 'subscription', tenantId],
    queryFn: () => billingService.subscription(tenantId!),
    enabled: !!tenantId,
  })
  const checkout = useMutation({
    mutationFn: billingService.createSubscription,
    onSuccess: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
    onError: (error: Error) =>
      toast.error('Não foi possível abrir o pagamento.', {
        description: error.message,
      }),
  })
  const completedFields = fields.filter((field) =>
    Boolean(profile.data?.[field.name]),
  ).length
  const complete = completedFields === fields.length

  if (!tenantId || profile.isLoading || subscription.isLoading)
    return (
      <main className="bg-muted/20 min-h-full p-6">
        <div className="mx-auto max-w-6xl space-y-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-80" />
        </div>
      </main>
    )
  if (hasBlockingQueryError(profile.isError, profile.data) || !profile.data)
    return (
      <main className="grid min-h-full place-items-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="text-destructive mx-auto mb-3 size-9" />
          <h2 className="font-semibold">Dados de cobrança indisponíveis</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Tente carregar novamente. Se o problema continuar, fale com o
            suporte.
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => profile.refetch()}
          >
            <RefreshCw /> Tentar novamente
          </Button>
        </div>
      </main>
    )

  const data = profile.data
  const completion = Math.round((completedFields / fields.length) * 100)
  const status = getStatus(subscription.data?.status)
  const paymentLink = subscription.data?.payment_link
  const actionLabel =
    subscription.data?.status === 'active'
      ? 'Plano ativo'
      : paymentLink
        ? 'Continuar pagamento'
        : 'Ativar agora'
  const action = () =>
    paymentLink
      ? window.open(paymentLink, '_blank', 'noopener,noreferrer')
      : checkout.mutate()

  return (
    <main className="bg-muted/20 min-h-full px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          eyebrow="Assinatura"
          title="Plano e faturamento"
          subtitle="Mantenha o UseComanda ativo para sua equipe vender, produzir e acompanhar resultados sem interrupções."
        />

        <section className="relative overflow-hidden rounded-2xl bg-[oklch(0.19_0.025_220)] p-6 text-white shadow-sm sm:p-8">
          <div className="absolute -top-24 -right-16 size-64 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-[1fr_21rem] lg:items-center">
            <div>
              <Badge className="mb-4 border-white/15 bg-white/10 text-white">
                <Sparkles /> Plano completo
              </Badge>
              <h2 className="max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl">
                Uma operação mais rápida custa menos que um erro por dia.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-white/65">
                Cardápio, pedidos, equipe e visão gerencial em um único fluxo.
                Sem taxa por venda e sem limite de funcionários.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm">
                {[
                  'Cardápio e preços centralizados',
                  'Acessos por função',
                  'Atualizações em tempo real',
                ].map((benefit) => (
                  <span
                    key={benefit}
                    className="flex items-center gap-2 text-white/85"
                  >
                    <span className="grid size-5 place-items-center rounded-full bg-emerald-400/15 text-emerald-300">
                      <Check className="size-3" />
                    </span>
                    {benefit}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
              <p className="text-sm text-white/60">Plano mensal</p>
              <div className="mt-1 flex items-end gap-1">
                <span className="text-4xl font-semibold tracking-tight">
                  R$ 139,90
                </span>
                <span className="pb-1 text-sm text-white/55">/mês</span>
              </div>
              <p className="mt-2 text-xs text-white/50">
                Cobrança mensal via PIX. Cancele quando quiser.
              </p>
              <Button
                className="mt-5 h-10 w-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300"
                disabled={
                  !complete ||
                  checkout.isPending ||
                  subscription.data?.status === 'active'
                }
                onClick={action}
              >
                {checkout.isPending && <Spinner data-icon="inline-start" />}
                {checkout.isPending ? 'Preparando pagamento…' : actionLabel}
                {!checkout.isPending &&
                  subscription.data?.status !== 'active' && <ArrowRight />}
              </Button>
              {!complete && (
                <button
                  className="mt-3 w-full text-center text-xs text-amber-200 hover:underline"
                  onClick={() => setBillingOpen(true)}
                >
                  Complete os dados fiscais para ativar
                </button>
              )}
            </div>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.35fr_1fr]">
          <Card className="gap-0 py-0">
            <CardHeader className="border-b py-4">
              <CardTitle className="flex items-center justify-between">
                <span>Status da assinatura</span>
                <Badge
                  variant={
                    status.tone === 'danger'
                      ? 'destructive'
                      : status.tone === 'success'
                        ? 'secondary'
                        : 'outline'
                  }
                >
                  {status.label}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 py-5">
              <div
                className={`flex gap-3 rounded-xl border p-4 ${status.tone === 'success' ? 'border-emerald-500/20 bg-emerald-500/5' : status.tone === 'danger' ? 'border-destructive/20 bg-destructive/5' : 'bg-muted/30'}`}
              >
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-full ${status.tone === 'success' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'}`}
                >
                  {status.tone === 'success' ? (
                    <CheckCircle2 />
                  ) : (
                    <CreditCard />
                  )}
                </span>
                <div>
                  <p className="font-medium">{status.label}</p>
                  <p className="text-muted-foreground mt-0.5 text-sm">
                    {status.description}
                  </p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <p className="text-muted-foreground text-xs">Valor mensal</p>
                  <p className="mt-1 font-semibold">R$ 139,90</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-muted-foreground text-xs">
                    Próximo vencimento
                  </p>
                  <p className="mt-1 font-semibold">
                    {subscription.data?.current_period_end
                      ? new Intl.DateTimeFormat('pt-BR').format(
                          new Date(subscription.data.current_period_end),
                        )
                      : 'Após a ativação'}
                  </p>
                </div>
              </div>
              {subscription.data?.status !== 'active' && (
                <Button
                  className="w-full"
                  disabled={!complete || checkout.isPending}
                  onClick={action}
                >
                  {checkout.isPending && <Spinner data-icon="inline-start" />}
                  {actionLabel}
                  {!checkout.isPending && <ArrowRight />}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="gap-0 py-0">
            <CardHeader className="border-b py-4">
              <CardTitle className="flex items-center justify-between">
                <span>Cadastro fiscal</span>
                <Badge variant={complete ? 'secondary' : 'outline'}>
                  {complete ? 'Completo' : `${completion}%`}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 py-5">
              <div>
                <div className="bg-muted h-2 overflow-hidden rounded-full">
                  <div
                    className="bg-primary h-full rounded-full transition-all"
                    style={{ width: `${completion}%` }}
                  />
                </div>
                <p className="text-muted-foreground mt-2 text-xs">
                  {complete
                    ? 'Tudo pronto para emitir cobranças.'
                    : `Faltam ${fields.length - completedFields} campos para ativar o plano.`}
                </p>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <FileText className="text-muted-foreground size-4" />
                  <span className="min-w-0 truncate">
                    {data.cpf_cnpj || 'Documento não informado'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="text-muted-foreground size-4" />
                  <span className="min-w-0 truncate">
                    {data.cidade && data.uf
                      ? `${data.cidade}, ${data.uf}`
                      : 'Endereço incompleto'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <ShieldCheck className="text-muted-foreground size-4" />
                  <span>Dados protegidos e usados só na cobrança</span>
                </div>
              </div>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => setBillingOpen(true)}
              >
                {complete ? 'Editar dados fiscais' : 'Completar cadastro'}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="bg-card flex gap-3 rounded-xl border p-4">
            <LockKeyhole className="text-muted-foreground size-5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Pagamento seguro</p>
              <p className="text-muted-foreground mt-1 text-xs leading-5">
                Checkout processado pelo Asaas.
              </p>
            </div>
          </div>
          <div className="bg-card flex gap-3 rounded-xl border p-4">
            <UsersRound className="text-muted-foreground size-5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Equipe sem limite</p>
              <p className="text-muted-foreground mt-1 text-xs leading-5">
                Adicione acessos conforme crescer.
              </p>
            </div>
          </div>
          <div className="bg-card flex gap-3 rounded-xl border p-4">
            <CreditCard className="text-muted-foreground size-5 shrink-0" />
            <div>
              <p className="text-sm font-medium">Sem taxa por venda</p>
              <p className="text-muted-foreground mt-1 text-xs leading-5">
                Preço previsível todos os meses.
              </p>
            </div>
          </div>
        </div>

        <Dialog
          open={billingOpen || Boolean(profile.data && !complete)}
          onOpenChange={(open) => {
            if (open || complete) setBillingOpen(open)
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Dados de faturamento</DialogTitle>
              <DialogDescription>
                Essas informações são usadas para criar a cobrança e emitir
                documentos fiscais.
              </DialogDescription>
            </DialogHeader>
            <BillingForm
              profile={data}
              tenantId={tenantId}
              onClose={() => setBillingOpen(false)}
              canCancel={complete}
            />
          </DialogContent>
        </Dialog>
      </div>
    </main>
  )
}
